/* eslint-disable no-param-reassign */
import { DragEndEvent } from '@dnd-kit/core';
import store, { AppDispatch } from 'app/store';
import {
  openingPlacementArgs,
  openingPlacementRun,
  resolveGridSpaceDropData,
  resolveOpeningDropEdge,
  type GridSpaceDropData
} from 'components/structure-page/2d/grid-space-drop-data';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AirHandlerTypeCodeType,
  ConfigurationType,
  ExposureDirectionType,
  FootprintPolygonsType,
  GridObjectType,
  GridSpaceDataType,
  LevelType,
  PositionType,
  SelectedSystemType,
  SpaceType
} from 'data/types';
import {
  addNewConfiguration,
  setConfigurations
} from 'features/configurations';
import {
  addNewLevelToStructure,
  addNewSpaceToLevel,
  beginUndoBatch,
  deleteStructureItem,
  endUndoBatch,
  moveElectricalPanelToLevel,
  moveSpaceToStructure,
  selectSpaceExtensionRecords,
  setSpaceExtensionRecords,
  setStructure,
  unplaceElectricalPanel,
  unplaceExistingSystemComponent,
  updateElectricalPanel,
  updateExistingSystemComponent,
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure';
import {
  reconcileSpaceWallsRequested,
  wallReconcileRequestsFor
} from 'features/structure/structure-reconcile-actions';
import {
  addApplianceToSpace,
  addDoorToWall,
  addSkylightToSpace,
  addWindowToWall,
  removeSkylightsFromSpace
} from 'features/structure/structure-thunks';
import { setActiveItem } from 'features/ui';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import { createElement } from 'react';
import {
  AIR_HANDLER_UNAVAILABLE_REASON,
  getSectionRestrictionReasons
} from 'utils/air-handler-restrictions';
import {
  condenserHasZoneCapacity,
  configurationZoneCapacityMessage
} from 'utils/condenser-zone-capacity';
import {
  calculateHeightOffGround,
  createCondenser,
  findZoneContainingSpace,
  markCondensersStale
} from 'utils/configurations';
import { getItem } from 'utils/data';
import {
  airHandlerMoveChangesZone,
  moveAirHandlerToGridPosition,
  moveCondenserToGridPosition
} from 'utils/equipment-move';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import {
  isUserPositionedOnPlacement,
  snapAirHandlerIntoSpace,
  zoneGridSpaces
} from 'utils/air-handler-placement';
import {
  determineCondenserPlacement,
  findNearestExternalEdge,
  findNearestSpace,
  getCondenserPlacementType,
  getContainingSpace,
  isInsideAnySpace,
  moveOutsideSpaces
} from 'utils/grid';
import { resolveZoneType } from 'utils/resolve-zone-type';
import { maybePromptAndApplyJoistWidth } from 'utils/joist-width-confirmation';
import {
  canPlaceIncomingSpaceOnRemainder,
  collisionSpacesForMove,
  commitMovedSpaceGeometry,
  deleteUserSpacesFromPalette,
  rebuildRemainderAfterUserMove,
  RemainderSyncState,
  tryPlaceIncomingSpaceOnRemainder
} from 'utils/leftover-space-sync';
import { isOpeningDragActiveType } from 'utils/opening-collision';
import { OPENING_PRESETS } from 'utils/opening-drag';
import { clearLiveOpeningDragPlacements } from 'utils/opening-drag-live-placement';
import { EMPTY_OPENING_RUN_BOUNDS } from 'utils/opening-position';
import { OpeningRunBoundsType } from 'utils/opening-position-types';
import { findOpeningRunLayout } from 'utils/opening-run-wall';
import {
  defaultCondenserType,
  defaultConfigurationType
} from 'utils/design-equipment-types';
import { isHybridOnlyPreference, isPtacPreference } from 'utils/project';
import {
  collectPtacRooms,
  placePtacUnitOnRoom,
  unplacePtacUnit
} from 'utils/ptac-configuration';
import { ptacLevelContextFor } from 'utils/ptac-level-context';
import { ptacRoomForDrop } from 'utils/ptac-unit-drop';
import { openingRunBoundsForSpaceEdge } from 'utils/space-opening-run';
import {
  addItemToWalls,
  deleteEdgeItem,
  DRAG_TYPE_TO_AIR_HANDLER_CODE,
  findLevelBySpaceId,
  findMinimalMoveToAvoidOverlap,
  findSectionBySpaceId,
  findSpaceById,
  findSpaceInStructure,
  fitSpaceAmongOthers,
  getAirHandlerTypeLabelFromCode,
  isDetachedSpaceItem,
  normalizeGridPosition,
  removeItemFromWalls
} from 'utils/structure';
import { structureEditResetsDesigns } from 'utils/structure-edit-reset';
import { structureOrientationSetting } from 'utils/structure-orientation';
import {
  confirmationModal,
  createActiveItem,
  notification,
  warningModal
} from 'utils/ui';
import { WallOpeningItemType } from 'utils/wall-opening-item-types';
import { isAirHandlerTypeAllowedInZone } from 'utils/zone-restrictions';
import {
  clampRectToFootprints,
  findPolygonForPoints
} from './footprint-polygons';
import {
  boundingBoxOfVertices,
  buildOtherSpacePolygonsAbsolute,
  buildSpaceShapeWithFootprintExtension,
  extendPolygonShapeToFootprint,
  isFootprintFullForNewSpace,
  isPolygonInsideAnyFootprint,
  isPolygonInsideFootprint,
  isRectangleBboxVertices,
  isRectangularVertices,
  polygonArea,
  polygonOverlapsAny,
  revertExtendedEdges
} from './space-polygon';

function remainderSyncStateFromStore(): RemainderSyncState {
  const state = store.getState();
  return {
    structure: state.structure.structure,
    autoLayoutSpaceIds: state.structure.autoLayoutSpaceIds,
    catalogAttributes: state.data.attributes,
    footprint: state.structure.footprint
  };
}

type ActiveDraggedItemType = {
  id: string;
  label: string;
  type: string;
} | null;

export function getActiveDraggedItem(
  activeId: string | null,
  activeDragData: any
): ActiveDraggedItemType {
  if (!activeId || !activeDragData) {
    return null;
  }

  if (
    activeDragData.type === 'grid-space' ||
    activeDragData.type === 'grid-condenser' ||
    activeDragData.type === 'grid-electrical-panel' ||
    activeDragData.type === 'grid-existing-equipment' ||
    activeDragData.type === 'grid-air-handler' ||
    activeDragData.type === 'grid-window' ||
    activeDragData.type === 'grid-door'
  ) {
    return null;
  }

  // Placement-step sidebar drags (AHA-6681 batch 2): map onto the existing
  // grid-space-overlay preview branches for placed equipment/panels.
  if (activeDragData.type === 'place-existing-equipment') {
    return {
      id: activeId,
      label: activeDragData.label ?? 'EQ',
      type: activeDragData.gridObjectType ?? 'existing-furnace'
    };
  }
  if (activeDragData.type === 'place-electrical-panel') {
    return { id: activeId, label: 'EP', type: 'electrical-panel' };
  }

  const typeToResult: Record<string, { label: string; type: string }> = {
    'grid-skylight': { label: 'Skylight', type: 'grid-skylight' },
    'new-space': { label: activeDragData.label, type: 'space' },
    'new-condenser': { label: 'C', type: 'condenser' },
    'new-ptac-unit': { label: 'PT', type: 'condenser' },
    'new-condenser-ground': { label: 'C', type: 'condenser-ground' },
    'new-condenser-wall': { label: 'C', type: 'condenser-wall' },
    'new-condenser-roof': { label: 'C', type: 'condenser-roof' },
    'new-air-handler-high-wall': { label: 'HW', type: 'air-handler-high-wall' },
    'new-air-handler-low-wall': { label: 'LW', type: 'air-handler-low-wall' },
    'new-air-handler-floor-ceiling': {
      label: 'FC',
      type: 'air-handler-floor-ceiling'
    },
    'new-air-handler-ceiling-cassette': {
      label: 'CC',
      type: 'air-handler-ceiling-cassette'
    },
    'new-air-handler-ceiling-cassette-slim-fit': {
      label: 'CCS',
      type: 'air-handler-ceiling-cassette-slim-fit'
    },
    'new-air-handler-concealed-duct': {
      label: 'CD',
      type: 'air-handler-concealed-duct'
    },
    'new-air-handler-ducted': {
      label: 'DH',
      type: 'air-handler-ducted'
    },
    'new-window-small': { label: 'Small Window', type: 'window-small' },
    'new-window-medium': { label: 'Medium Window', type: 'window-medium' },
    'new-window-large': { label: 'Large Window', type: 'window-large' },
    'new-door-wood': { label: 'Wooden Door', type: 'door-wood' },
    'new-door-metal': { label: 'Metal Door', type: 'door-metal' },
    'new-door-pet': { label: 'Pet Door', type: 'door-pet' },
    'new-door-glass-single': {
      label: 'Single Glass Door',
      type: 'door-glass-single'
    },
    'new-door-glass-double': {
      label: 'Double Glass Door',
      type: 'door-glass-double'
    },
    'new-door-garage-single': {
      label: 'Single Garage Door',
      type: 'door-garage-single'
    },
    'new-door-garage-double': {
      label: 'Double Garage Door',
      type: 'door-garage-double'
    },
    'new-appliance': {
      label: activeDragData.label || 'Appliance',
      type: 'appliance'
    },
    'new-skylight-small': { label: 'Small Skylight', type: 'skylight-small' },
    'new-skylight-medium': {
      label: 'Medium Skylight',
      type: 'skylight-medium'
    },
    'new-skylight-large': { label: 'Large Skylight', type: 'skylight-large' }
  };

  const result = typeToResult[activeDragData.type];
  if (result) {
    return { id: activeId, label: result.label, type: result.type };
  }

  return null;
}

const CONDENSER_DRAG_TYPES = new Set([
  'new-condenser',
  'new-condenser-ground',
  'new-condenser-wall',
  'new-condenser-roof'
]);

export function isCondenserOrAirHandlerDragType(dragType: unknown): boolean {
  return (
    typeof dragType === 'string' &&
    (CONDENSER_DRAG_TYPES.has(dragType) ||
      dragType in DRAG_TYPE_TO_AIR_HANDLER_CODE)
  );
}

type RectType = { left: number; top: number };

type DraggedItemType = {
  id: string;
  label: string;
  type:
    | 'new-level'
    | 'new-space'
    | 'new-condenser'
    | 'new-ptac-unit'
    | 'new-condenser-ground'
    | 'new-condenser-wall'
    | 'new-condenser-roof'
    | 'new-air-handler-high-wall'
    | 'new-air-handler-low-wall'
    | 'new-air-handler-floor-ceiling'
    | 'new-air-handler-ceiling-cassette'
    | 'new-air-handler-ceiling-cassette-slim-fit'
    | 'new-air-handler-concealed-duct'
    | 'new-air-handler-ducted'
    | 'new-window-small'
    | 'new-window-medium'
    | 'new-window-large'
    | 'new-door-wood'
    | 'new-door-metal'
    | 'new-door-pet'
    | 'new-door-glass-single'
    | 'new-door-glass-double'
    | 'new-door-garage-single'
    | 'new-door-garage-double'
    | 'new-appliance'
    | 'new-skylight-small'
    | 'new-skylight-medium'
    | 'new-skylight-large'
    | 'grid-window'
    | 'grid-door'
    | 'grid-skylight'
    | 'section'
    | 'space'
    | 'grid-space'
    | 'grid-condenser'
    | 'grid-air-handler'
    | 'grid-electrical-panel'
    | 'grid-existing-equipment'
    | 'grid-lineset-point'
    | 'place-existing-equipment'
    | 'place-electrical-panel';
  itemId?: string | number;
  gridObjectType?: 'existing-condenser' | 'existing-furnace';
  currentEdge?: 'top' | 'right' | 'bottom' | 'left';
  spaceId?: string;
  skylightUniqueIds?: string[];
  space?: GridSpaceDataType;
  condenser?: GridObjectType;
  electricalPanel?: GridObjectType;
  existingEquipment?: GridObjectType;
  airHandler?: GridObjectType;
  sectionId?: string;
  spacesInSection?: number;
  item?: any;
};

type HandleDragEndParams = {
  event: DragEndEvent;
  dispatch: AppDispatch;
  structure: LevelType[];
  levels: LevelType[];
  selectedLevelIndex: number;
  configurations: ConfigurationType[];
  activeConfigIndex: number;
  selectedSystems: SelectedSystemType[];
  draggedSectionSpaces: GridSpaceDataType[];
  isMultiSelectSpaceDrag?: boolean;
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[];
  setActiveId: (id: string | null) => void;
  setActiveDragData: (data: any) => void;
  setDraggedSectionSpaces: (spaces: GridSpaceDataType[]) => void;
  structureOrientation?: ExposureDirectionType;
  zoom?: number;
  panOffset?: { x: number; y: number };
  autoResizeSpacesToFit?: boolean;
  footprintPolygons?: FootprintPolygonsType | null;
  blockAirHandlerDrops?: boolean;
};

function handleWindowDrop(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  overData: GridSpaceDropData
) {
  const dropEdge = resolveOpeningDropEdge(overData);
  if (dropEdge) {
    let windowType: 'small' | 'medium' | 'large' = 'small';
    if (draggedItem.type === 'new-window-small') {
      windowType = 'small';
    } else if (draggedItem.type === 'new-window-medium') {
      windowType = 'medium';
    } else if (draggedItem.type === 'new-window-large') {
      windowType = 'large';
    }

    dispatch(
      addWindowToWall({
        spaceId: overData.spaceId,
        edge: dropEdge,
        windowType,
        run: openingPlacementRun(overData),
        ...openingPlacementArgs(overData)
      })
    );
  }
}

function handleDoorDrop(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  overData: GridSpaceDropData,
  doorType: 'solid' | 'pet' = 'solid',
  material?: 'Wood' | 'Metal'
) {
  const dropEdge = resolveOpeningDropEdge(overData);
  if (dropEdge) {
    dispatch(
      addDoorToWall({
        spaceId: overData.spaceId,
        edge: dropEdge,
        doorType,
        material,
        run: openingPlacementRun(overData),
        ...openingPlacementArgs(overData)
      })
    );
  }
}

function handleApplianceDrop(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  overData: {
    spaceId: string;
  }
) {
  if (draggedItem.itemId && typeof draggedItem.itemId === 'number') {
    dispatch(
      addApplianceToSpace({
        spaceId: overData.spaceId,
        applianceItemId: draggedItem.itemId
      })
    );
  }
}

function handleMoveWindowOrDoor(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  overData: GridSpaceDropData,
  structure: LevelType[],
  structureOrientation: ExposureDirectionType | undefined
) {
  const dropEdge = resolveOpeningDropEdge(overData);
  if (!dropEdge || !draggedItem.currentEdge || !draggedItem.spaceId) {
    return;
  }

  const isWindow = draggedItem.type === 'grid-window';
  const isSameSpace = draggedItem.spaceId === overData.spaceId;
  const itemType = isWindow ? 'window' : 'door';

  if (
    isSameSpace &&
    draggedItem.currentEdge === dropEdge &&
    openingPlacementArgs(overData).positionOnWall === undefined
  ) {
    return;
  }

  const sourceSpace = findSpaceInStructure(structure, draggedItem.spaceId);

  if (!sourceSpace?.sizingAttributes || !draggedItem.itemId) {
    return;
  }

  const sourceWalls = [
    ...sourceSpace.sizingAttributes.aboveGradeWalls,
    ...sourceSpace.sizingAttributes.belowGradeWalls
  ];
  const itemFromWalls: WallOpeningItemType | undefined = isWindow
    ? sourceWalls
        .flatMap((wall) => wall.verticalGlass || [])
        .find((glass) => glass.uniqueId === draggedItem.itemId)
    : sourceWalls
        .flatMap((wall) => wall.solidDoors || [])
        .find((door) => door.uniqueId === draggedItem.itemId);
  const itemToMove =
    itemFromWalls ?? (draggedItem.item as WallOpeningItemType | undefined);

  if (!itemToMove) {
    return;
  }

  const destinationSpace = isSameSpace
    ? sourceSpace
    : findSpaceInStructure(structure, overData.spaceId);
  const destinationLevel = destinationSpace
    ? findLevelBySpaceId(structure, destinationSpace.uniqueId)
    : null;
  const destinationLayout = destinationSpace
    ? findOpeningRunLayout(
        destinationSpace,
        destinationLevel,
        structureOrientation,
        openingPlacementRun(overData)
      )
    : null;
  const targetWallUniqueId = destinationLayout?.wallUniqueId;
  const runBounds: OpeningRunBoundsType =
    destinationLayout?.run ??
    (destinationSpace
      ? openingRunBoundsForSpaceEdge(
          destinationSpace,
          dropEdge,
          destinationLevel,
          structureOrientation
        )
      : EMPTY_OPENING_RUN_BOUNDS);

  const movedItem = {
    ...itemToMove,
    ...openingPlacementArgs(overData)
  };

  const updatedSourceSizingAttributes = {
    ...sourceSpace.sizingAttributes
  };

  updatedSourceSizingAttributes.aboveGradeWalls = removeItemFromWalls(
    updatedSourceSizingAttributes.aboveGradeWalls,
    undefined,
    draggedItem.itemId,
    itemType
  );

  updatedSourceSizingAttributes.belowGradeWalls = removeItemFromWalls(
    updatedSourceSizingAttributes.belowGradeWalls,
    undefined,
    draggedItem.itemId,
    itemType
  );

  if (isSameSpace) {
    const isBasement =
      findLevelBySpaceId(structure, sourceSpace.uniqueId)?.type === 'basement';

    if (isBasement) {
      updatedSourceSizingAttributes.belowGradeWalls = addItemToWalls(
        updatedSourceSizingAttributes.belowGradeWalls,
        dropEdge,
        movedItem,
        itemType,
        structureOrientation,
        runBounds,
        targetWallUniqueId
      );
    } else {
      updatedSourceSizingAttributes.aboveGradeWalls = addItemToWalls(
        updatedSourceSizingAttributes.aboveGradeWalls,
        dropEdge,
        movedItem,
        itemType,
        structureOrientation,
        runBounds,
        targetWallUniqueId
      );
    }

    dispatch(
      updateStructureItem({
        uniqueId: sourceSpace.uniqueId,
        sizingAttributes: updatedSourceSizingAttributes
      })
    );
  } else {
    dispatch(beginUndoBatch());
    dispatch(
      updateStructureItem({
        uniqueId: sourceSpace.uniqueId,
        sizingAttributes: updatedSourceSizingAttributes
      })
    );

    const targetSpace = findSpaceInStructure(structure, overData.spaceId);

    if (targetSpace?.sizingAttributes) {
      const updatedTargetSizingAttributes = {
        ...targetSpace.sizingAttributes
      };

      const isBasement =
        findLevelBySpaceId(structure, targetSpace.uniqueId)?.type ===
        'basement';

      if (isBasement) {
        updatedTargetSizingAttributes.belowGradeWalls = addItemToWalls(
          updatedTargetSizingAttributes.belowGradeWalls,
          dropEdge,
          movedItem,
          itemType,
          structureOrientation,
          runBounds,
          targetWallUniqueId
        );
      } else {
        updatedTargetSizingAttributes.aboveGradeWalls = addItemToWalls(
          updatedTargetSizingAttributes.aboveGradeWalls,
          dropEdge,
          movedItem,
          itemType,
          structureOrientation,
          runBounds,
          targetWallUniqueId
        );
      }

      dispatch(
        updateStructureItem({
          uniqueId: targetSpace.uniqueId,
          sizingAttributes: updatedTargetSizingAttributes
        })
      );
    }
    dispatch(endUndoBatch());
  }
}

function handleSectionSpacesDrag(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  draggedSectionSpaces: GridSpaceDataType[],
  structure: LevelType[],
  selectedLevelIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 },
  footprintPolygons: FootprintPolygonsType | null = null
) {
  const draggedSpace = draggedSectionSpaces.find(
    (s) => s.id === draggedItem.space!.id
  );
  const minX = Math.min(...draggedSectionSpaces.map((s) => s.position.x));
  const minY = Math.min(...draggedSectionSpaces.map((s) => s.position.y));

  if (!draggedSpace) return;

  const offsetX = draggedSpace.position.x - minX;
  const offsetY = draggedSpace.position.y - minY;
  const overlayX =
    ((translatedRect?.left || 0) - containerRect.left - panOffset.x) / zoom;
  const overlayY =
    ((translatedRect?.top || 0) - containerRect.top - panOffset.y) / zoom;
  const visualDraggedSpaceX = overlayX + offsetX;
  const visualDraggedSpaceY = overlayY + offsetY;
  const draggedSpaceTargetX =
    Math.round(visualDraggedSpaceX) / STRUCTURE_GRID_SIZE;
  const draggedSpaceTargetY =
    Math.round(visualDraggedSpaceY) / STRUCTURE_GRID_SIZE;
  let deltaX =
    draggedSpaceTargetX - draggedSpace.position.x / STRUCTURE_GRID_SIZE;
  let deltaY =
    draggedSpaceTargetY - draggedSpace.position.y / STRUCTURE_GRID_SIZE;

  const currentLevelSpaces = getGridSpacesForLevel(selectedLevelIndex);
  const draggedSpaceIds = new Set(draggedSectionSpaces.map((s) => s.spaceId));
  const sectionLevel = findLevelBySpaceId(
    store.getState().structure.structure,
    draggedItem.space!.spaceId
  );
  const otherSpaces = collisionSpacesForMove(
    currentLevelSpaces,
    draggedSpaceIds,
    sectionLevel,
    store.getState().structure.autoLayoutSpaceIds
  );

  const allOtherPositions = otherSpaces.map((s) =>
    normalizeGridPosition(s.position)
  );

  const movingSpacePositions = draggedSectionSpaces
    .map((s) => normalizeGridPosition(s.position))
    .map((pos) => ({
      ...pos,
      x: pos.x + deltaX,
      y: pos.y + deltaY
    }));

  const adjustment = findMinimalMoveToAvoidOverlap(
    movingSpacePositions,
    allOtherPositions
  );

  deltaX += adjustment.deltaX;
  deltaY += adjustment.deltaY;

  const allDetached = draggedSectionSpaces.every(
    (s) => s.itemId != null && isDetachedSpaceItem(s.itemId)
  );

  if (footprintPolygons && footprintPolygons.length > 0 && !allDetached) {
    const allFit = draggedSectionSpaces.every((s) => {
      const pos = normalizeGridPosition(s.position);
      const moved = {
        x: pos.x + deltaX,
        y: pos.y + deltaY,
        width: pos.width,
        height: pos.height
      };
      const clamped = clampRectToFootprints(moved, footprintPolygons);
      return (
        clamped.x === moved.x &&
        clamped.y === moved.y &&
        clamped.width === moved.width &&
        clamped.height === moved.height
      );
    });

    if (!allFit) {
      return;
    }
  }

  const newStructure = produce(structure, (draft) => {
    draft.forEach((level) => {
      level.sections.forEach((section) => {
        section.spaces.forEach((space) => {
          const draggedSpaceData = draggedSectionSpaces.find(
            (s) => s.spaceId === space.uniqueId
          );
          if (draggedSpaceData && space.position) {
            space.position = {
              ...(space as any).position,
              x: (space as any).position.x + deltaX,
              y: (space as any).position.y + deltaY
            };
          }
        });
      });
    });
  });

  dispatch(beginUndoBatch());
  dispatch(setStructure(newStructure));
  dispatch(
    reconcileSpaceWallsRequested(
      wallReconcileRequestsFor(structure, draggedSpaceIds)
    )
  );
  rebuildRemainderAfterUserMove(
    dispatch,
    sectionLevel,
    draggedSpaceIds,
    remainderSyncStateFromStore()
  );
  dispatch(endUndoBatch());
}

function handleSingleSpaceDrag(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  selectedLevelIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 },
  autoResizeSpacesToFit: boolean = true,
  footprintPolygons: FootprintPolygonsType | null = null
) {
  const x =
    Math.round(
      ((translatedRect?.left || 0) - containerRect.left - panOffset.x) / zoom
    ) / STRUCTURE_GRID_SIZE;
  const y =
    Math.round(
      ((translatedRect?.top || 0) - containerRect.top - panOffset.y) / zoom
    ) / STRUCTURE_GRID_SIZE;

  const effectiveFootprints =
    draggedItem.space?.itemId != null &&
    isDetachedSpaceItem(draggedItem.space.itemId)
      ? []
      : footprintPolygons ?? [];

  const currentLevelSpaces = getGridSpacesForLevel(selectedLevelIndex);
  const draggedSpaceId = draggedItem.space!.spaceId;
  const moveLevel = findLevelBySpaceId(
    store.getState().structure.structure,
    draggedSpaceId
  );
  const otherSpaces = collisionSpacesForMove(
    currentLevelSpaces,
    new Set([draggedSpaceId]),
    moveLevel,
    store.getState().structure.autoLayoutSpaceIds
  );

  const naturalRectWidth =
    (draggedItem.space!.naturalWidth || draggedItem.space!.position.width) /
    STRUCTURE_GRID_SIZE;
  const naturalRectHeight =
    (draggedItem.space!.naturalHeight || draggedItem.space!.position.height) /
    STRUCTURE_GRID_SIZE;

  const droppedSpaceDimensions = {
    x,
    y,
    width: naturalRectWidth,
    height: naturalRectHeight
  };

  const verticesPixels = draggedItem.space!.vertices ?? [];
  const widthPixels = draggedItem.space!.position.width;
  const heightPixels = draggedItem.space!.position.height;
  const isRect = isRectangularVertices(
    verticesPixels,
    widthPixels,
    heightPixels
  );

  if (!isRect && verticesPixels.length >= 3) {
    const verticesFeetOriginal = verticesPixels.map((v) => ({
      x: v.x / STRUCTURE_GRID_SIZE,
      y: v.y / STRUCTURE_GRID_SIZE
    }));
    const currentX = (draggedItem.space!.position.x ?? 0) / STRUCTURE_GRID_SIZE;
    const currentY = (draggedItem.space!.position.y ?? 0) / STRUCTURE_GRID_SIZE;

    const existingRecords = selectSpaceExtensionRecords(
      store.getState(),
      draggedItem.space!.spaceId
    );

    const currentAbsVerts = verticesFeetOriginal.map((v) => ({
      x: currentX + v.x,
      y: currentY + v.y
    }));
    const revertedAbsVerts = revertExtendedEdges(
      currentAbsVerts,
      existingRecords
    );

    const reverted = revertedAbsVerts !== currentAbsVerts;
    let baseVerticesFeet: PositionType[];
    let baseCurrentX: number;
    let baseCurrentY: number;

    if (reverted) {
      const bbReverted = boundingBoxOfVertices(revertedAbsVerts);
      baseCurrentX = bbReverted.minX;
      baseCurrentY = bbReverted.minY;
      baseVerticesFeet = revertedAbsVerts.map((v) => ({
        x: v.x - bbReverted.minX,
        y: v.y - bbReverted.minY
      }));
    } else {
      baseVerticesFeet = verticesFeetOriginal;
      baseCurrentX = currentX;
      baseCurrentY = currentY;
    }
    const desiredX = x;
    const desiredY = y;

    const otherPolys = buildOtherSpacePolygonsAbsolute(
      otherSpaces,
      draggedItem.space!.spaceId
    );

    const computeAbsVerts = (cx: number, cy: number) =>
      baseVerticesFeet.map((v) => ({ x: cx + v.x, y: cy + v.y }));
    const isValidPosition = (verts: PositionType[]) => {
      if (!isPolygonInsideAnyFootprint(verts, effectiveFootprints)) {
        return false;
      }
      if (polygonOverlapsAny(verts, otherPolys)) {
        return false;
      }
      return true;
    };

    let candidateX = desiredX;
    let candidateY = desiredY;
    let absVerticesFeet = computeAbsVerts(candidateX, candidateY);

    if (!isValidPosition(absVerticesFeet)) {
      let low = 0;
      let high = 1;
      let foundValid = false;
      for (let i = 0; i < 24; i += 1) {
        const mid = (low + high) / 2;
        const tryX = baseCurrentX + (desiredX - baseCurrentX) * mid;
        const tryY = baseCurrentY + (desiredY - baseCurrentY) * mid;
        if (isValidPosition(computeAbsVerts(tryX, tryY))) {
          low = mid;
          foundValid = true;
        } else {
          high = mid;
        }
      }
      if (!foundValid) return;
      candidateX = baseCurrentX + (desiredX - baseCurrentX) * low;
      candidateY = baseCurrentY + (desiredY - baseCurrentY) * low;
      absVerticesFeet = computeAbsVerts(candidateX, candidateY);
    }

    let finalAbsVerticesFeet = absVerticesFeet;
    let newExtensionRecords: ReturnType<typeof selectSpaceExtensionRecords> =
      [];
    const containingFootprint = findPolygonForPoints(
      absVerticesFeet,
      effectiveFootprints
    );
    if (containingFootprint) {
      const extended = extendPolygonShapeToFootprint(
        absVerticesFeet,
        containingFootprint,
        otherPolys
      );
      if (
        extended &&
        extended.vertices.length >= 3 &&
        isPolygonInsideFootprint(extended.vertices, containingFootprint) &&
        !polygonOverlapsAny(extended.vertices, otherPolys)
      ) {
        finalAbsVerticesFeet = extended.vertices;
        newExtensionRecords = extended.extendedEdges;
      }
    }

    const bb = boundingBoxOfVertices(finalAbsVerticesFeet);
    const positionFeet = { x: bb.minX, y: bb.minY };
    const relativeVertices = finalAbsVerticesFeet.map((v) => ({
      x: v.x - bb.minX,
      y: v.y - bb.minY
    }));
    const isRectangle = isRectangleBboxVertices(
      relativeVertices,
      bb.width,
      bb.height
    );
    const newWidth = isRectangle ? bb.width : undefined;
    const newLength = isRectangle ? bb.height : undefined;

    dispatch(beginUndoBatch());
    const polygonMoveCommitted = commitMovedSpaceGeometry(
      dispatch,
      draggedItem.space!.spaceId,
      {
        position: positionFeet,
        width: newWidth,
        length: newLength,
        vertices: relativeVertices
      },
      Math.round(polygonArea(relativeVertices)),
      remainderSyncStateFromStore()
    );
    if (polygonMoveCommitted) {
      dispatch(
        setSpaceExtensionRecords({
          spaceId: draggedItem.space!.spaceId,
          records: newExtensionRecords
        })
      );
    }
    dispatch(endUndoBatch());
    return;
  }

  const allOtherPositions = otherSpaces.map((s) =>
    normalizeGridPosition(s.position)
  );
  const allOtherPolygons = buildOtherSpacePolygonsAbsolute(
    otherSpaces,
    draggedItem.space!.spaceId
  );

  if (autoResizeSpacesToFit) {
    const fitted = fitSpaceAmongOthers(
      droppedSpaceDimensions,
      allOtherPositions,
      undefined,
      allOtherPolygons,
      effectiveFootprints
    );

    if (!fitted) {
      warningModal(
        'That spot is already taken — drop the space somewhere else on the footprint.'
      );
      return;
    }

    const shapeUpdate = buildSpaceShapeWithFootprintExtension({
      fitted,
      footprintPolygons: effectiveFootprints,
      otherSpaces,
      excludeSpaceId: draggedItem.space!.spaceId,
      forceWrite: true,
      dropRect: droppedSpaceDimensions
    });

    dispatch(beginUndoBatch());
    const fittedMoveCommitted = commitMovedSpaceGeometry(
      dispatch,
      draggedItem.space!.spaceId,
      {
        position: { x: shapeUpdate.position.x, y: shapeUpdate.position.y },
        width: shapeUpdate.width,
        length: shapeUpdate.length,
        vertices: shapeUpdate.vertices ?? []
      },
      Math.ceil(polygonArea(shapeUpdate.vertices ?? [])),
      remainderSyncStateFromStore()
    );
    if (fittedMoveCommitted) {
      dispatch(
        setSpaceExtensionRecords({
          spaceId: draggedItem.space!.spaceId,
          records: shapeUpdate.extendedEdges
        })
      );
    }
    dispatch(endUndoBatch());

    return;
  }

  const adjustment = findMinimalMoveToAvoidOverlap(
    droppedSpaceDimensions,
    allOtherPositions
  );

  let finalPosition = {
    x: droppedSpaceDimensions.x + adjustment.deltaX,
    y: droppedSpaceDimensions.y + adjustment.deltaY,
    width: droppedSpaceDimensions.width,
    height: droppedSpaceDimensions.height
  };

  finalPosition = clampRectToFootprints(finalPosition, effectiveFootprints);

  const shapeUpdate = buildSpaceShapeWithFootprintExtension({
    fitted: finalPosition,
    footprintPolygons: effectiveFootprints,
    otherSpaces,
    excludeSpaceId: draggedItem.space!.spaceId,
    forceWrite: true,
    dropRect: droppedSpaceDimensions
  });

  dispatch(beginUndoBatch());
  try {
    const defaultMoveCommitted = commitMovedSpaceGeometry(
      dispatch,
      draggedItem.space!.spaceId,
      {
        position: {
          x: shapeUpdate.position.x,
          y: shapeUpdate.position.y
        },
        width: shapeUpdate.width,
        length: shapeUpdate.length,
        vertices: shapeUpdate.vertices ?? []
      },
      Math.ceil(polygonArea(shapeUpdate.vertices ?? [])),
      remainderSyncStateFromStore()
    );
    if (defaultMoveCommitted) {
      dispatch(
        setSpaceExtensionRecords({
          spaceId: draggedItem.space!.spaceId,
          records: shapeUpdate.extendedEdges
        })
      );
    }
  } finally {
    dispatch(endUndoBatch());
  }
}

function handleCondenserDrag(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  configurations: ConfigurationType[],
  activeConfigIndex: number,
  levels: LevelType[],
  selectedLevelIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
) {
  const x = Math.round(
    ((translatedRect?.left || 0) - containerRect.left - panOffset.x) /
      zoom /
      STRUCTURE_GRID_SIZE
  );
  const y = Math.round(
    ((translatedRect?.top || 0) - containerRect.top - panOffset.y) /
      zoom /
      STRUCTURE_GRID_SIZE
  );

  const activeConfig = configurations[activeConfigIndex];
  if (!activeConfig) return;

  moveCondenserToGridPosition({
    dispatch,
    configurations,
    configurationUniqueId: activeConfig.uniqueId,
    condenserUniqueId: draggedItem.condenser!.id,
    x,
    y,
    levels,
    levelIndex: selectedLevelIndex,
    gridSpaces: getGridSpacesForLevel(selectedLevelIndex)
  });
}

function handleAirHandlerDrag(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  configurations: ConfigurationType[],
  activeConfigIndex: number,
  selectedLevelIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number,
  panOffset: { x: number; y: number },
  blockZoneChange: boolean
) {
  const x =
    Math.round(
      ((translatedRect?.left || 0) - containerRect.left - panOffset.x) / zoom
    ) / STRUCTURE_GRID_SIZE;
  const y =
    Math.round(
      ((translatedRect?.top || 0) - containerRect.top - panOffset.y) / zoom
    ) / STRUCTURE_GRID_SIZE;

  const activeConfig = configurations[activeConfigIndex];
  if (!activeConfig) return;

  const move = {
    configurations,
    configurationUniqueId: activeConfig.uniqueId,
    zoneItemId: draggedItem.airHandler!.id.replace('-air-handler', ''),
    x,
    y,
    gridSpaces: getGridSpacesForLevel(selectedLevelIndex)
  };
  if (blockZoneChange && airHandlerMoveChangesZone(move)) return;

  moveAirHandlerToGridPosition({ dispatch, ...move });
}

function handleElectricalPanelDrag(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
) {
  const x = Math.round(
    ((translatedRect?.left || 0) - containerRect.left - panOffset.x) /
      zoom /
      STRUCTURE_GRID_SIZE
  );
  const y = Math.round(
    ((translatedRect?.top || 0) - containerRect.top - panOffset.y) /
      zoom /
      STRUCTURE_GRID_SIZE
  );

  dispatch(
    updateElectricalPanel({
      uniqueId: draggedItem.electricalPanel!.id,
      updates: {
        position: {
          x,
          y
        }
      }
    })
  );
}

async function handleNewSpaceDrop(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  levels: LevelType[],
  selectedLevelIndex: number,
  configurations: ConfigurationType[],
  selectedSystems: SelectedSystemType[],
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 },
  footprintPolygons: FootprintPolygonsType | null = null
) {
  const x = Math.round(
    ((translatedRect?.left || 0) - containerRect.left - panOffset.x) /
      zoom /
      STRUCTURE_GRID_SIZE
  );
  const y = Math.round(
    ((translatedRect?.top || 0) - containerRect.top - panOffset.y) /
      zoom /
      STRUCTURE_GRID_SIZE
  );

  const effectiveFootprints =
    typeof draggedItem.itemId === 'number' &&
    isDetachedSpaceItem(draggedItem.itemId)
      ? []
      : footprintPolygons ?? [];

  const dropLevel = levels[selectedLevelIndex];
  const canPlaceOnRemainder = canPlaceIncomingSpaceOnRemainder(
    dropLevel,
    store.getState().structure.autoLayoutSpaceIds
  );

  if (effectiveFootprints.length > 0 && dropLevel && !canPlaceOnRemainder) {
    const currentLevelSpaces = getGridSpacesForLevel(selectedLevelIndex);
    if (currentLevelSpaces.length > 0) {
      const otherSpacePolygons =
        buildOtherSpacePolygonsAbsolute(currentLevelSpaces);
      if (isFootprintFullForNewSpace(effectiveFootprints, otherSpacePolygons)) {
        warningModal(
          'There is no room on the footprint for any more spaces. Only detached spaces can be added.'
        );
        return;
      }
    }
  }

  const addSpaceToCurrentLevel = async () => {
    let targetLevelId: string;

    if (levels.length === 0) {
      const newLevel = await dispatch(
        addNewLevelToStructure({ levelType: 'level' })
      ).unwrap();

      if (!newLevel) {
        return;
      }

      targetLevelId = newLevel.uniqueId;
    } else {
      const currentLevel = levels[selectedLevelIndex];

      if (!currentLevel) {
        return;
      }

      targetLevelId = currentLevel.uniqueId;
    }

    const newSpace = await dispatch(
      addNewSpaceToLevel({
        levelId: targetLevelId,
        spaceItemId: draggedItem.itemId as number
      })
    ).unwrap();

    if (newSpace) {
      const spaceWidth = 10;
      const spaceLength = 6;
      const spaceX = x;
      const spaceY = y;

      const newSpaceDimensions = {
        x: spaceX,
        y: spaceY,
        width: spaceWidth,
        height: spaceLength
      };

      const incoming = clampRectToFootprints(
        newSpaceDimensions,
        effectiveFootprints
      );

      const remainderResult = tryPlaceIncomingSpaceOnRemainder(
        dispatch,
        targetLevelId,
        newSpace.uniqueId,
        incoming,
        remainderSyncStateFromStore(),
        () => store.getState().structure.structure
      );
      if (remainderResult === 'blocked') {
        return;
      }
      if (remainderResult === 'placed') {
        if (getItem(newSpace.itemId)?.code === 'custom') {
          const activeItem = createActiveItem({
            type: 'space',
            item: newSpace
          });
          if (activeItem) {
            dispatch(setActiveItem(activeItem));
          }
        }
        return;
      }

      const currentLevelSpaces = getGridSpacesForLevel(selectedLevelIndex);
      const otherSpaces = currentLevelSpaces.filter(
        (s) => s.spaceId !== newSpace.uniqueId
      );
      const allOtherPositions = otherSpaces.map((s) =>
        normalizeGridPosition(s.position)
      );
      const allOtherPolygons = buildOtherSpacePolygonsAbsolute(
        otherSpaces,
        newSpace.uniqueId
      );

      const fitted = fitSpaceAmongOthers(
        incoming,
        allOtherPositions,
        undefined,
        allOtherPolygons,
        effectiveFootprints
      );

      if (!fitted) {
        dispatch(deleteStructureItem({ uniqueId: newSpace.uniqueId }));
        warningModal(
          'That spot is already taken — drop the space somewhere else on the footprint.'
        );
        return;
      }

      const shapeUpdate = buildSpaceShapeWithFootprintExtension({
        fitted,
        footprintPolygons: effectiveFootprints,
        otherSpaces,
        excludeSpaceId: newSpace.uniqueId,
        forceWrite: true,
        dropRect: incoming
      });

      dispatch(beginUndoBatch());
      dispatch(
        updateStructureItem({
          uniqueId: newSpace.uniqueId,
          position: { x: shapeUpdate.position.x, y: shapeUpdate.position.y },
          width: shapeUpdate.width,
          length: shapeUpdate.length,
          vertices: shapeUpdate.vertices
        } as SpaceType)
      );

      dispatch(
        updateStructureItemAttribute({
          uniqueId: newSpace.uniqueId,
          attributeCode: 'square_footage',
          value: Math.ceil(polygonArea(shapeUpdate.vertices ?? []))
        })
      );
      dispatch(
        setSpaceExtensionRecords({
          spaceId: newSpace.uniqueId,
          records: shapeUpdate.extendedEdges
        })
      );
      dispatch(endUndoBatch());

      if (getItem(newSpace.itemId)?.code === 'custom') {
        const activeItem = createActiveItem({ type: 'space', item: newSpace });
        if (activeItem) {
          dispatch(setActiveItem(activeItem));
        }
      }
    }
  };

  if (structureEditResetsDesigns(configurations, selectedSystems)) {
    confirmationModal(
      'Are you sure you want to add a space? This will reset all configurations and selected systems!',
      addSpaceToCurrentLevel
    );
  } else {
    await addSpaceToCurrentLevel();
  }
}

export const PTAC_UNIT_DROP_MISSED_ROOM = 'Drop the PTAC unit onto a room.';
export const PTAC_UNIT_DROP_UNZONED_ROOM =
  'Add the room to a zone first, then drop a PTAC unit on it.';

function handleNewPtacUnitDrop(
  dispatch: AppDispatch,
  structure: LevelType[],
  selectedLevelIndex: number,
  configurations: ConfigurationType[],
  activeConfigIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number,
  panOffset: { x: number; y: number },
  dropTargetSpaceId?: string
) {
  const activeConfig = configurations[activeConfigIndex];
  if (!activeConfig || !isPtacPreference()) return;

  const unitPosition = {
    x: Math.round(
      ((translatedRect?.left || 0) - containerRect.left - panOffset.x) /
        zoom /
        STRUCTURE_GRID_SIZE
    ),
    y: Math.round(
      ((translatedRect?.top || 0) - containerRect.top - panOffset.y) /
        zoom /
        STRUCTURE_GRID_SIZE
    )
  };
  const levelSpaces = getGridSpacesForLevel(selectedLevelIndex);
  const gridSpace =
    (dropTargetSpaceId
      ? levelSpaces.find((space) => space.spaceId === dropTargetSpaceId)
      : undefined) ?? ptacRoomForDrop(unitPosition, levelSpaces);
  const room = gridSpace
    ? collectPtacRooms(structure).find(
        (entry) => entry.space.uniqueId === gridSpace.spaceId
      )
    : undefined;
  if (!room) {
    notification(PTAC_UNIT_DROP_MISSED_ROOM, 'warning');
    return;
  }

  const zoned = findZoneContainingSpace(activeConfig, room.space.uniqueId);
  if (!zoned) {
    notification(PTAC_UNIT_DROP_UNZONED_ROOM, 'warning');
    return;
  }

  const placed = placePtacUnitOnRoom(
    activeConfig,
    zoned.condenser.uniqueId,
    room,
    structure,
    ptacLevelContextFor(
      structure,
      room.level,
      levelSpaces,
      structureOrientationSetting(store.getState().project.projectSettings)
    ),
    unitPosition
  );
  if (!placed) return;
  dispatch(
    setConfigurations(
      configurations.map((config) =>
        config.uniqueId === activeConfig.uniqueId ? placed : config
      )
    )
  );
}

async function handleNewCondenserDrop(
  dispatch: AppDispatch,
  levels: LevelType[],
  selectedLevelIndex: number,
  configurations: ConfigurationType[],
  activeConfigIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  dragType: string,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
) {
  const gridSquares = 3;
  const objectSize = gridSquares * STRUCTURE_GRID_SIZE;

  let x = Math.round(
    ((translatedRect?.left || 0) - containerRect.left - panOffset.x) /
      zoom /
      STRUCTURE_GRID_SIZE
  );
  let y = Math.round(
    ((translatedRect?.top || 0) - containerRect.top - panOffset.y) /
      zoom /
      STRUCTURE_GRID_SIZE
  );

  const currentLevel = levels[selectedLevelIndex];
  if (!currentLevel) return;

  const gridSpaces = getGridSpacesForLevel(selectedLevelIndex);

  const placementType =
    getCondenserPlacementType(dragType) ??
    determineCondenserPlacement(
      levels,
      selectedLevelIndex,
      { x, y },
      objectSize,
      gridSpaces
    );

  if (placementType === 'ground') {
    if (isInsideAnySpace({ x, y }, objectSize, gridSpaces)) {
      const newPosition = moveOutsideSpaces({ x, y }, objectSize, gridSpaces);
      x = newPosition.x;
      y = newPosition.y;
    }
  } else if (placementType === 'wall') {
    const externalEdge = findNearestExternalEdge(
      { x, y },
      objectSize,
      gridSpaces,
      getEffectiveFootprintPolygons(currentLevel)
    );
    if (externalEdge) {
      x = externalEdge.x;
      y = externalEdge.y;
    }
  } else if (placementType === 'roof') {
    if (!isInsideAnySpace({ x, y }, objectSize, gridSpaces)) {
      const nearestSpace = findNearestSpace({ x, y }, objectSize, gridSpaces);
      if (nearestSpace) {
        x = nearestSpace.position.x / STRUCTURE_GRID_SIZE;
        y = nearestSpace.position.y / STRUCTURE_GRID_SIZE;
      }
    }
  }

  let currentConfigurations = configurations;
  let activeConfig = currentConfigurations[activeConfigIndex];
  if (!activeConfig) {
    const newConfig = await dispatch(
      addNewConfiguration({
        name: 'Configuration 1',
        type: defaultConfigurationType('ductless')
      })
    ).unwrap();
    if (!newConfig) return;
    activeConfig = newConfig;
    currentConfigurations = [newConfig];
  }

  const visibleCondensersCount = activeConfig.condensers.length;
  const newCondenser = createCondenser({
    name: `Condenser ${visibleCondensersCount + 1}`,
    type: defaultCondenserType(),
    position: {
      x,
      y
    },
    levelUniqueId: currentLevel.uniqueId,
    placementType,
    heightOffGround: calculateHeightOffGround(
      placementType,
      currentLevel.uniqueId,
      levels
    )
  });
  newCondenser.modified = true;

  const updatedConfigurations = produce(currentConfigurations, (draft) => {
    const config = draft.find((c) => c.uniqueId === activeConfig.uniqueId);
    if (config) {
      config.modified = true;
      config.condensers.push(newCondenser);
    } else {
      draft.push({
        ...activeConfig,
        condensers: [newCondenser],
        modified: true
      });
    }
  });

  dispatch(setConfigurations(updatedConfigurations));
}

async function handleNewAirHandlerDrop(
  dispatch: AppDispatch,
  structure: LevelType[],
  levels: LevelType[],
  selectedLevelIndex: number,
  configurations: ConfigurationType[],
  activeConfigIndex: number,
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  containerRect: RectType,
  translatedRect: RectType | null,
  airHandlerTypeCode: AirHandlerTypeCodeType,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 },
  targetSpaceId?: string
) {
  const gridSquares = 3;
  const objectSize = gridSquares * STRUCTURE_GRID_SIZE;

  const currentLevel = levels[selectedLevelIndex];
  if (!currentLevel) return;

  const gridSpaces = getGridSpacesForLevel(selectedLevelIndex);

  let x =
    Math.round(
      ((translatedRect?.left || 0) - containerRect.left - panOffset.x) / zoom
    ) / STRUCTURE_GRID_SIZE;
  let y =
    Math.round(
      ((translatedRect?.top || 0) - containerRect.top - panOffset.y) / zoom
    ) / STRUCTURE_GRID_SIZE;

  let targetSpace: GridSpaceDataType | null = null;
  if (targetSpaceId) {
    targetSpace =
      gridSpaces.find(
        (s) => s.id === targetSpaceId || s.spaceId === targetSpaceId
      ) ?? null;
  }
  if (!targetSpace) {
    targetSpace = getContainingSpace({ x, y }, objectSize, gridSpaces);
    if (!targetSpace) {
      targetSpace = findNearestSpace({ x, y }, objectSize, gridSpaces);
    }
  }

  if (!targetSpace) return;

  const finalAirHandlerTypeCode = airHandlerTypeCode;

  let existingZoneForSnapping: {
    zoneItems: { structureItemId: string }[];
  } | null = null;
  const existingActiveConfig = configurations[activeConfigIndex];
  if (existingActiveConfig) {
    const existingResult = findZoneContainingSpace(
      existingActiveConfig,
      targetSpace.spaceId
    );
    if (existingResult?.zone) {
      existingZoneForSnapping = existingResult.zone;
    }
  }

  ({ x, y } = snapAirHandlerIntoSpace({
    airHandlerTypeCode: finalAirHandlerTypeCode,
    position: { x, y },
    objectSize,
    space: targetSpace,
    wallSnapOptions: { allGridSpaces: gridSpaces, structure },
    zoneSpaces: zoneGridSpaces(existingZoneForSnapping, gridSpaces)
  }));

  let currentConfigurations = configurations;
  let activeConfig = currentConfigurations[activeConfigIndex];
  if (!activeConfig) {
    const newConfig = await dispatch(
      addNewConfiguration({
        name: 'Configuration 1',
        type: defaultConfigurationType(
          isHybridOnlyPreference() ? 'hybrid' : 'ductless'
        )
      })
    ).unwrap();
    if (!newConfig) return;
    activeConfig = newConfig;
    currentConfigurations = [newConfig];
  }

  const existingZoneResult = findZoneContainingSpace(
    activeConfig,
    targetSpace.spaceId
  );

  const zone = existingZoneResult?.zone;
  const condenser = existingZoneResult?.condenser;

  if (!zone || !condenser) return;

  // AHA-6784 design A: on the hybrid path the zone type follows the placed
  // air handler (mirrors the server's deriveZoneType persistence semantics).
  // Never written outside hybrid, and only when the value differs, so the
  // ductless/ducted paths stay byte-stable.
  const hybridTargetZoneType = resolveZoneType({
    airHandlerTypeCode: finalAirHandlerTypeCode
  });
  const hybridZoneTypeFlip =
    isHybridOnlyPreference() && zone.type !== hybridTargetZoneType;

  const performAdd = () => {
    const updatedConfigurations = produce(currentConfigurations, (draft) => {
      const config = draft.find((c) => c.uniqueId === activeConfig.uniqueId);
      if (!config) return;
      const draftCondenser = config.condensers.find(
        (c) => c.uniqueId === condenser.uniqueId
      );
      if (!draftCondenser) return;
      const draftZone = draftCondenser.zones.find(
        (z) => z.uniqueId === zone.uniqueId
      );
      if (draftZone) {
        if (hybridZoneTypeFlip) {
          draftZone.type = hybridTargetZoneType;
        }
        draftZone.airHandler = {
          uniqueId: nanoid(5),
          airHandlerTypeCode: finalAirHandlerTypeCode,
          positionX: x,
          positionY: y,
          valid: true,
          errorMessage: null,
          warningMessage: null,
          modified: true,
          ...(isUserPositionedOnPlacement(finalAirHandlerTypeCode)
            ? { userPositioned: true }
            : {})
        } as any;
        draftZone.modified = true;
        // Autosave writes condenserTypeCode from condenser.type. Promote
        // (or demote) to match the zones so matching-AH can arm the
        // ducted-only valid-systems path. Hybrid-path only.
        if (isHybridOnlyPreference()) {
          const nextCondenserType = draftCondenser.zones.some(
            (z) => z.type === 'ducted'
          )
            ? 'hybrid'
            : 'ductless';
          if (draftCondenser.type !== nextCondenserType) {
            draftCondenser.type = nextCondenserType;
            draftCondenser.modified = true;
          }
        }
        config.modified = true;
      }
    });

    const finalConfigurations = updatedConfigurations.map((cfg) =>
      cfg.uniqueId === activeConfig.uniqueId
        ? markCondensersStale(cfg, [condenser.uniqueId])
        : cfg
    );

    dispatch(setConfigurations(finalConfigurations));

    const airHandlerLabel = getAirHandlerTypeLabelFromCode(
      finalAirHandlerTypeCode
    );
    if (airHandlerLabel && zone.zoneItems) {
      zone.zoneItems.forEach((zoneItem) => {
        dispatch(
          updateStructureItemAttribute({
            uniqueId: zoneItem.structureItemId,
            attributeCode: 'preferred_air_handler_type',
            value: airHandlerLabel
          })
        );
      });
    }
  };

  const restrictionReasons: string[] = [];
  const targetSection = findSectionBySpaceId(structure, targetSpace.spaceId);
  if (targetSection) {
    restrictionReasons.push(
      ...getSectionRestrictionReasons(
        finalAirHandlerTypeCode,
        targetSection.spaces.map((sp) => sp.uniqueId),
        structure
      )
    );
  }

  if (
    !isAirHandlerTypeAllowedInZone(
      finalAirHandlerTypeCode,
      condenser,
      zone.uniqueId
    )
  ) {
    restrictionReasons.push(AIR_HANDLER_UNAVAILABLE_REASON);
  }

  if (hybridZoneTypeFlip) {
    restrictionReasons.push(
      hybridTargetZoneType === 'ducted'
        ? 'Placing a ducted air handler converts this zone to a ducted zone; system availability will be re-checked.'
        : 'Placing a ductless air handler converts this zone to a ductless zone; system availability will be re-checked.'
    );
  }

  if (restrictionReasons.length === 0) {
    performAdd();
    return;
  }

  const confirmationMessage = createElement(
    'div',
    null,
    createElement(
      'p',
      null,
      "We don't think this air handler type will work here:"
    ),
    createElement(
      'ul',
      {
        style: {
          textAlign: 'left',
          marginTop: 8,
          paddingLeft: 20,
          listStyleType: 'disc'
        }
      },
      restrictionReasons.map((reason) =>
        createElement('li', { key: reason }, reason)
      )
    )
  );

  confirmationModal(
    confirmationMessage,
    performAdd,
    'Add this air handler anyway?',
    'Cancel',
    'Add anyway'
  );
}

function handleExistingEquipmentDrag(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
) {
  const x = Math.round(
    ((translatedRect?.left || 0) - containerRect.left - panOffset.x) /
      zoom /
      STRUCTURE_GRID_SIZE
  );
  const y = Math.round(
    ((translatedRect?.top || 0) - containerRect.top - panOffset.y) /
      zoom /
      STRUCTURE_GRID_SIZE
  );

  dispatch(
    updateExistingSystemComponent({
      uniqueId: draggedItem.existingEquipment!.id,
      position: { x, y }
    })
  );
}

function computeGridDropPosition(
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number,
  panOffset: { x: number; y: number }
): { x: number; y: number } {
  return {
    x: Math.round(
      ((translatedRect?.left || 0) - containerRect.left - panOffset.x) /
        zoom /
        STRUCTURE_GRID_SIZE
    ),
    y: Math.round(
      ((translatedRect?.top || 0) - containerRect.top - panOffset.y) /
        zoom /
        STRUCTURE_GRID_SIZE
    )
  };
}

// Placement step (AHA-6681 batch 2): an UNPLACED component dragged from the
// sidebar list lands on the currently-viewed level.
function handlePlaceExistingEquipmentDrop(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  levels: LevelType[],
  selectedLevelIndex: number,
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
) {
  const currentLevel = levels[selectedLevelIndex];
  if (!currentLevel) {
    return;
  }
  const position = computeGridDropPosition(
    containerRect,
    translatedRect,
    zoom,
    panOffset
  );

  dispatch(
    updateExistingSystemComponent({
      uniqueId: draggedItem.itemId as string,
      position,
      levelUniqueId: currentLevel.uniqueId
    })
  );
}

// Placement step (AHA-6681 batch 2): panels always belong to a level, so a
// drop while viewing a DIFFERENT level than the panel's current one moves
// the panel to the viewed level (batch 6 removed the modal's floor select
// in anticipation of exactly this).
function handlePlaceElectricalPanelDrop(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  structure: LevelType[],
  levels: LevelType[],
  selectedLevelIndex: number,
  containerRect: RectType,
  translatedRect: RectType | null,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
) {
  const currentLevel = levels[selectedLevelIndex];
  if (!currentLevel) {
    return;
  }
  const uniqueId = draggedItem.itemId as string;
  const position = computeGridDropPosition(
    containerRect,
    translatedRect,
    zoom,
    panOffset
  );

  const panelLevel = structure.find((level) =>
    level.electricalPanels.some((panel) => panel.uniqueId === uniqueId)
  );
  if (!panelLevel) {
    return;
  }

  if (panelLevel.uniqueId === currentLevel.uniqueId) {
    dispatch(updateElectricalPanel({ uniqueId, updates: { position } }));
    return;
  }

  dispatch(
    moveElectricalPanelToLevel({
      uniqueId,
      targetLevelUniqueId: currentLevel.uniqueId,
      position
    })
  );
}

function handleSpaceTypeDropOnGrid(
  dispatch: AppDispatch,
  draggedItem: DraggedItemType,
  configurations: ConfigurationType[],
  selectedSystems: SelectedSystemType[]
) {
  if (structureEditResetsDesigns(configurations, selectedSystems)) {
    confirmationModal(
      'Are you sure you want to move this space? This will reset all configurations and selected systems!',
      () => {
        dispatch(moveSpaceToStructure({ spaceId: draggedItem.id }));
      }
    );
  } else {
    dispatch(moveSpaceToStructure({ spaceId: draggedItem.id }));
  }
}

export function createHandleDragEnd(params: HandleDragEndParams) {
  const {
    event,
    dispatch,
    structure,
    levels,
    selectedLevelIndex,
    configurations,
    activeConfigIndex,
    selectedSystems,
    draggedSectionSpaces,
    isMultiSelectSpaceDrag = false,
    getGridSpacesForLevel,
    setActiveId,
    setActiveDragData,
    setDraggedSectionSpaces,
    structureOrientation,
    zoom = 1,
    panOffset = { x: 0, y: 0 },
    autoResizeSpacesToFit = true,
    footprintPolygons = null,
    blockAirHandlerDrops = false
  } = params;

  const { active, over, collisions } = event;
  setActiveId(null);
  setActiveDragData(null);
  const hasSectionSpaces = draggedSectionSpaces.length > 0;
  setDraggedSectionSpaces([]);

  function finishDragEnd() {
    if (!over) {
      return;
    }

    const draggedItem = active.data.current as DraggedItemType;

    if (over.id === 'item-list-container') {
      if (draggedItem.type === 'grid-space' && draggedItem.space) {
        const uniqueIds =
          isMultiSelectSpaceDrag && draggedSectionSpaces.length > 1
            ? Array.from(
                new Set(draggedSectionSpaces.map((space) => space.spaceId))
              )
            : [draggedItem.space.spaceId];
        dispatch(deleteUserSpacesFromPalette(uniqueIds));
        return;
      }

      if (draggedItem.type === 'grid-condenser' && draggedItem.condenser) {
        const activeConfig = configurations[activeConfigIndex];

        if (activeConfig && isPtacPreference()) {
          const unplaced = unplacePtacUnit(
            activeConfig,
            draggedItem.condenser.id
          );
          dispatch(
            setConfigurations(
              configurations.map((config) =>
                config.uniqueId === activeConfig.uniqueId ? unplaced : config
              )
            )
          );
          return;
        }

        if (activeConfig) {
          let droppedZones = 0;
          const updatedConfigurations = produce(configurations, (draft) => {
            const config = draft.find(
              (c) => c.uniqueId === activeConfig.uniqueId
            );
            if (config) {
              const condenserIndex = config.condensers.findIndex(
                (c) => c.uniqueId === draggedItem.condenser!.id
              );
              if (condenserIndex === -1) return;

              const condenserToDelete = config.condensers[condenserIndex];

              const remainingCondensers = config.condensers.filter(
                (_, i) => i !== condenserIndex
              );

              if (
                remainingCondensers.length > 0 &&
                condenserToDelete.zones.length > 0
              ) {
                const deletedPos = condenserToDelete.position;
                const sortedSurvivorIds = [...remainingCondensers]
                  .sort((a, b) => {
                    if (!deletedPos) return 0;
                    const aDist = a.position
                      ? Math.hypot(
                          a.position.x - deletedPos.x,
                          a.position.y - deletedPos.y
                        )
                      : Infinity;
                    const bDist = b.position
                      ? Math.hypot(
                          b.position.x - deletedPos.x,
                          b.position.y - deletedPos.y
                        )
                      : Infinity;
                    return aDist - bDist;
                  })
                  .map((c) => c.uniqueId);

                for (const zone of condenserToDelete.zones) {
                  const target = sortedSurvivorIds
                    .map((id) =>
                      config.condensers.find((c) => c.uniqueId === id)
                    )
                    .find((c) => c && condenserHasZoneCapacity(c));
                  if (target) {
                    target.zones.push({ ...zone });
                  } else {
                    droppedZones += 1;
                  }
                }
              }

              config.condensers.splice(condenserIndex, 1);

              if (config.lineSets) {
                config.lineSets = config.lineSets.filter(
                  (ls) => ls.condenserUniqueId !== draggedItem.condenser!.id
                );
              }
            }
          });

          dispatch(setConfigurations(updatedConfigurations));

          if (droppedZones > 0) {
            notification(
              `${configurationZoneCapacityMessage(
                activeConfig
              )} ${droppedZones} zone(s) could not be reassigned.`,
              'warning'
            );
          }
        }

        return;
      }

      if (draggedItem.type === 'grid-air-handler' && draggedItem.airHandler) {
        const activeConfig = configurations[activeConfigIndex];

        if (activeConfig) {
          const zoneItemId = draggedItem.airHandler.id.replace(
            '-air-handler',
            ''
          );

          let actualAirHandlerUniqueId: string | undefined;
          for (const condenser of activeConfig.condensers) {
            for (const zone of condenser.zones) {
              if (zone.zoneItems.some((zi) => zi.uniqueId === zoneItemId)) {
                actualAirHandlerUniqueId = zone.airHandler?.uniqueId;
                break;
              }
            }
            if (actualAirHandlerUniqueId) break;
          }

          let affectedCondenserId: string | undefined;
          let updatedConfigurations = produce(configurations, (draft) => {
            const config = draft.find(
              (c) => c.uniqueId === activeConfig.uniqueId
            );
            if (config) {
              if (config.lineSets && actualAirHandlerUniqueId) {
                config.lineSets = config.lineSets.filter(
                  (ls) => ls.airHandlerUniqueId !== actualAirHandlerUniqueId
                );
              }

              for (const condenser of config.condensers) {
                const zoneIndex = condenser.zones.findIndex((zone) =>
                  zone.zoneItems.some((zi) => zi.uniqueId === zoneItemId)
                );
                if (zoneIndex !== -1) {
                  affectedCondenserId = condenser.uniqueId;
                  delete condenser.zones[zoneIndex].airHandler;
                  break;
                }
              }
            }
          });

          updatedConfigurations = updatedConfigurations.map((config) =>
            config.uniqueId === activeConfig.uniqueId
              ? markCondensersStale(
                  config,
                  affectedCondenserId ? [affectedCondenserId] : []
                )
              : config
          );

          dispatch(setConfigurations(updatedConfigurations));
        }

        return;
      }

      // AHA-6681 batch 2: dragging placed equipment/panels back to the sidebar
      // UN-PLACES them (returns them to the placement list) instead of
      // deleting — entry/deletion lives on the Existing Equipment page, the
      // grid only owns placement.
      if (
        draggedItem.type === 'grid-electrical-panel' &&
        draggedItem.electricalPanel
      ) {
        dispatch(
          unplaceElectricalPanel({
            uniqueId: draggedItem.electricalPanel.id
          })
        );
        return;
      }

      if (
        draggedItem.type === 'grid-existing-equipment' &&
        draggedItem.existingEquipment
      ) {
        dispatch(
          unplaceExistingSystemComponent({
            uniqueId: draggedItem.existingEquipment.id
          })
        );
        return;
      }

      if (
        (draggedItem.type === 'grid-window' ||
          draggedItem.type === 'grid-door') &&
        draggedItem.spaceId &&
        draggedItem.currentEdge &&
        draggedItem.itemId
      ) {
        const structureSpace = findSpaceById(structure, draggedItem.spaceId);
        if (structureSpace?.sizingAttributes) {
          dispatch(
            updateStructureItem({
              uniqueId: structureSpace.uniqueId,
              sizingAttributes: deleteEdgeItem(
                structureSpace.sizingAttributes,
                draggedItem.currentEdge,
                draggedItem.itemId as string,
                draggedItem.type === 'grid-window',
                structureOrientation
              )
            })
          );
        }
        return;
      }

      if (
        draggedItem.type === 'grid-skylight' &&
        draggedItem.spaceId &&
        draggedItem.skylightUniqueIds?.length
      ) {
        dispatch(
          removeSkylightsFromSpace({
            spaceId: draggedItem.spaceId,
            skylightUniqueIds: draggedItem.skylightUniqueIds
          })
        );
        return;
      }

      return;
    }

    const isSpaceDropTarget =
      typeof over.id === 'string' && over.id.startsWith('drop-');
    const openingDropData = isOpeningDragActiveType(draggedItem.type)
      ? resolveGridSpaceDropData(over, collisions)
      : null;

    if (isSpaceDropTarget || openingDropData) {
      if (
        draggedItem.type === 'new-condenser-ground' ||
        draggedItem.type === 'new-condenser-wall' ||
        draggedItem.type === 'new-condenser-roof' ||
        draggedItem.type === 'grid-condenser' ||
        draggedItem.type === 'grid-electrical-panel' ||
        draggedItem.type === 'grid-existing-equipment' ||
        draggedItem.type === 'grid-lineset-point'
      ) {
        return;
      }

      const overData =
        openingDropData ?? (over.data.current as GridSpaceDropData);

      if (
        draggedItem.type === 'new-window-small' ||
        draggedItem.type === 'new-window-medium' ||
        draggedItem.type === 'new-window-large'
      ) {
        handleWindowDrop(dispatch, draggedItem, overData);
        return;
      }

      if (
        draggedItem.type === 'new-door-wood' ||
        draggedItem.type === 'new-door-metal' ||
        draggedItem.type === 'new-door-pet'
      ) {
        const doorType = draggedItem.type === 'new-door-pet' ? 'pet' : 'solid';
        const material =
          draggedItem.type === 'new-door-metal' ? 'Metal' : 'Wood';
        handleDoorDrop(dispatch, draggedItem, overData, doorType, material);
        return;
      }

      if (
        draggedItem.type === 'new-door-glass-single' ||
        draggedItem.type === 'new-door-glass-double'
      ) {
        const glassDropEdge = resolveOpeningDropEdge(overData);
        if (glassDropEdge) {
          const isDouble = draggedItem.type === 'new-door-glass-double';
          const glassDoor = OPENING_PRESETS[draggedItem.type];
          dispatch(
            addWindowToWall({
              spaceId: overData.spaceId,
              edge: glassDropEdge,
              fenestrationType: isDouble ? 'French Door' : 'Sliding Glass Door',
              widthOverride: glassDoor.widthFt,
              heightOverride: glassDoor.heightFt,
              run: openingPlacementRun(overData),
              ...openingPlacementArgs(overData)
            })
          );
        }
        return;
      }

      if (
        draggedItem.type === 'new-door-garage-single' ||
        draggedItem.type === 'new-door-garage-double'
      ) {
        const garageDropEdge = resolveOpeningDropEdge(overData);
        if (garageDropEdge) {
          const garageSize =
            draggedItem.type === 'new-door-garage-double' ? 'double' : 'single';
          dispatch(
            addDoorToWall({
              spaceId: overData.spaceId,
              edge: garageDropEdge,
              doorType: 'garage',
              garageSize,
              run: openingPlacementRun(overData),
              ...openingPlacementArgs(overData)
            })
          );
        }
        return;
      }

      if (draggedItem.type === 'new-appliance') {
        handleApplianceDrop(dispatch, draggedItem, overData);
        return;
      }

      if (
        draggedItem.type === 'new-skylight-small' ||
        draggedItem.type === 'new-skylight-medium' ||
        draggedItem.type === 'new-skylight-large'
      ) {
        const skylightTypeMap = {
          'new-skylight-small': 'small',
          'new-skylight-medium': 'medium',
          'new-skylight-large': 'large'
        } as const;
        dispatch(
          addSkylightToSpace({
            spaceId: overData.spaceId,
            skylightType: skylightTypeMap[draggedItem.type]
          })
        );
        return;
      }

      if (
        draggedItem.type === 'grid-window' ||
        draggedItem.type === 'grid-door'
      ) {
        handleMoveWindowOrDoor(
          dispatch,
          draggedItem,
          overData,
          structure,
          structureOrientation
        );
        return;
      }
    }

    const isOverDropTarget =
      typeof over.id === 'string' && over.id.startsWith('drop-');
    const isDraggingAirHandler =
      draggedItem.type === 'new-air-handler-high-wall' ||
      draggedItem.type === 'new-air-handler-low-wall' ||
      draggedItem.type === 'new-air-handler-floor-ceiling' ||
      draggedItem.type === 'new-air-handler-ceiling-cassette' ||
      draggedItem.type === 'new-air-handler-ceiling-cassette-slim-fit' ||
      draggedItem.type === 'new-air-handler-concealed-duct' ||
      draggedItem.type === 'new-air-handler-ducted' ||
      draggedItem.type === 'grid-air-handler';

    if (
      over.id === 'grid-container' ||
      (isOverDropTarget && draggedItem.type === 'grid-space') ||
      (isOverDropTarget && isDraggingAirHandler) ||
      (isOverDropTarget && draggedItem.type === 'new-ptac-unit')
    ) {
      let containerRect = over.rect;
      const translatedRect = active.rect.current.translated;
      const overData = over.data.current as { spaceId?: string } | undefined;
      let dropTargetSpaceId: string | undefined;

      if (isOverDropTarget && overData?.spaceId) {
        dropTargetSpaceId = overData.spaceId;
        if (draggedItem.type === 'grid-space') {
          const gridContainer = document.getElementById('structure-grid');
          if (gridContainer) {
            const gridRect = gridContainer.getBoundingClientRect();
            containerRect = {
              ...over.rect,
              left: gridRect.left,
              top: gridRect.top
            };
          }
        }
      }

      if (draggedItem.type === 'grid-space' && draggedItem.space) {
        if (hasSectionSpaces && draggedSectionSpaces.length > 1) {
          handleSectionSpacesDrag(
            dispatch,
            draggedItem,
            draggedSectionSpaces,
            structure,
            selectedLevelIndex,
            getGridSpacesForLevel,
            containerRect,
            translatedRect,
            zoom,
            panOffset,
            footprintPolygons
          );
        } else {
          handleSingleSpaceDrag(
            dispatch,
            draggedItem,
            selectedLevelIndex,
            getGridSpacesForLevel,
            containerRect,
            translatedRect,
            zoom,
            panOffset,
            autoResizeSpacesToFit,
            footprintPolygons
          );
        }
        return;
      }

      if (draggedItem.type === 'grid-condenser' && draggedItem.condenser) {
        handleCondenserDrag(
          dispatch,
          draggedItem,
          configurations,
          activeConfigIndex,
          levels,
          selectedLevelIndex,
          getGridSpacesForLevel,
          containerRect,
          translatedRect,
          zoom,
          panOffset
        );
        return;
      }

      if (draggedItem.type === 'grid-air-handler' && draggedItem.airHandler) {
        handleAirHandlerDrag(
          dispatch,
          draggedItem,
          configurations,
          activeConfigIndex,
          selectedLevelIndex,
          getGridSpacesForLevel,
          containerRect,
          translatedRect,
          zoom,
          panOffset,
          blockAirHandlerDrops
        );
        return;
      }

      if (
        draggedItem.type === 'grid-electrical-panel' &&
        draggedItem.electricalPanel
      ) {
        handleElectricalPanelDrag(
          dispatch,
          draggedItem,
          containerRect,
          translatedRect,
          zoom,
          panOffset
        );
        return;
      }

      if (
        draggedItem.type === 'grid-existing-equipment' &&
        draggedItem.existingEquipment
      ) {
        handleExistingEquipmentDrag(
          dispatch,
          draggedItem,
          containerRect,
          translatedRect,
          zoom,
          panOffset
        );
        return;
      }

      if (
        draggedItem.type === 'place-existing-equipment' &&
        draggedItem.itemId
      ) {
        handlePlaceExistingEquipmentDrop(
          dispatch,
          draggedItem,
          levels,
          selectedLevelIndex,
          containerRect,
          translatedRect,
          zoom,
          panOffset
        );
        return;
      }

      if (draggedItem.type === 'place-electrical-panel' && draggedItem.itemId) {
        handlePlaceElectricalPanelDrop(
          dispatch,
          draggedItem,
          structure,
          levels,
          selectedLevelIndex,
          containerRect,
          translatedRect,
          zoom,
          panOffset
        );
        return;
      }

      if (over.rect && draggedItem.type === 'new-space' && draggedItem.itemId) {
        handleNewSpaceDrop(
          dispatch,
          draggedItem,
          levels,
          selectedLevelIndex,
          configurations,
          selectedSystems,
          getGridSpacesForLevel,
          containerRect,
          translatedRect,
          zoom,
          panOffset,
          footprintPolygons
        );
        return;
      }

      if (draggedItem.type === 'new-ptac-unit') {
        handleNewPtacUnitDrop(
          dispatch,
          structure,
          selectedLevelIndex,
          configurations,
          activeConfigIndex,
          getGridSpacesForLevel,
          containerRect,
          translatedRect,
          zoom,
          panOffset,
          dropTargetSpaceId
        );
        return;
      }

      if (
        draggedItem.type === 'new-condenser' ||
        draggedItem.type === 'new-condenser-ground' ||
        draggedItem.type === 'new-condenser-wall' ||
        draggedItem.type === 'new-condenser-roof'
      ) {
        handleNewCondenserDrop(
          dispatch,
          levels,
          selectedLevelIndex,
          configurations,
          activeConfigIndex,
          getGridSpacesForLevel,
          containerRect,
          translatedRect,
          draggedItem.type,
          zoom,
          panOffset
        );
        return;
      }

      const airHandlerTypeCode =
        DRAG_TYPE_TO_AIR_HANDLER_CODE[draggedItem.type];
      if (airHandlerTypeCode) {
        if (blockAirHandlerDrops) return;
        (async () => {
          if (airHandlerTypeCode === 'cassette') {
            const shouldProceed = await maybePromptAndApplyJoistWidth(
              dispatch,
              store.getState().project.projectSettings
            );
            if (!shouldProceed) return;
          }
          handleNewAirHandlerDrop(
            dispatch,
            structure,
            levels,
            selectedLevelIndex,
            configurations,
            activeConfigIndex,
            getGridSpacesForLevel,
            containerRect,
            translatedRect,
            airHandlerTypeCode,
            zoom,
            panOffset,
            dropTargetSpaceId
          );
        })();
        return;
      }

      if (
        draggedItem.type === 'grid-lineset-point' ||
        (active.data.current as any)?.type === 'grid-lineset-point'
      ) {
        return;
      }

      if (draggedItem.type === 'space') {
        handleSpaceTypeDropOnGrid(
          dispatch,
          draggedItem,
          configurations,
          selectedSystems
        );
      }
    }
  }

  try {
    finishDragEnd();
  } finally {
    clearLiveOpeningDragPlacements();
  }
}
